import { FLAGS } from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Gate } from '../components/shell/gate.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Card } from '../components/ui/card.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';
import { Skeleton } from '../components/ui/skeleton.tsx';
import { describeError } from '../lib/api.ts';
import { api, type FlagView } from '../lib/client.ts';
import { useSession } from '../lib/session.tsx';
import { cn } from '../lib/utils.ts';

export function FlagsPage() {
  const session = useSession();
  const qc = useQueryClient();
  const flags = useQuery({ queryKey: ['flags'], queryFn: api.flags });
  const toggle = useMutation({
    mutationFn: (f: FlagView) => api.setFlag(f.name, !f.enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flags'] }),
  });
  const canManage = session.can('flag.manage');
  return (
    <Gate permission="flag.read">
      <div className="mx-auto max-w-4xl">
        <PageHeader
          title="Feature flags"
          description={`As resolved for you in ${session.me.organisation.name}. An override set here applies to the organisation; targeting rules live in flagd.`}
        />
        {flags.isError && (
          <p className="text-danger mb-3 text-sm" role="alert">
            {describeError(flags.error)}
          </p>
        )}
        {toggle.isError && (
          <p className="text-danger mb-3 text-sm" role="alert">
            {describeError(toggle.error)}
          </p>
        )}
        <Card className="overflow-hidden">
          {flags.isPending && (
            <div className="flex flex-col gap-3 p-5">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          )}
          <ul className="divide-border divide-y">
            {(flags.data ?? []).map((f) => (
              <li
                key={f.name}
                className="hover:bg-surface-hover/60 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-5 py-4 transition-colors"
              >
                <div className="min-w-0 flex-1 basis-64">
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="font-mono text-[13px] font-medium break-all">{f.name}</code>
                    {f.isDefault && <Badge variant="outline">default</Badge>}
                  </div>
                  <p className="text-muted-foreground mt-1 text-sm">{FLAGS[f.name].description}</p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <Badge
                    variant={f.enabled ? 'success' : 'secondary'}
                    className="min-w-11 justify-center"
                    data-testid={`flag-${f.name}`}
                  >
                    {f.enabled ? 'on' : 'off'}
                  </Badge>
                  {canManage && (
                    <button
                      type="button"
                      disabled={toggle.isPending}
                      onClick={() => toggle.mutate(f)}
                      aria-pressed={f.enabled}
                      aria-label={`Turn ${f.name} ${f.enabled ? 'off' : 'on'}`}
                      className={cn(
                        'focus-visible:ring-ring/40 relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:ring-3 focus-visible:outline-none disabled:cursor-wait disabled:opacity-60',
                        f.enabled ? 'bg-brand' : 'bg-border-hover',
                      )}
                    >
                      <span
                        className={cn(
                          'pointer-events-none block size-5 rounded-full bg-white shadow-sm transition-transform',
                          f.enabled ? 'translate-x-[22px]' : 'translate-x-0.5',
                        )}
                      />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </Gate>
  );
}
